import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  message, 
  Typography,
  Empty,
  Button,
  Space
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '@/services/api';
import { WorkRecord, Project, User, RecordType, RecordStatus } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

interface TeamLogsPageProps {}

const TeamLogs: React.FC<TeamLogsPageProps> = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [datesLoading, setDatesLoading] = useState(false);
  const [records, setRecords] = useState<WorkRecord[]>([]);
  const [currentDate, setCurrentDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [currentDateIndex, setCurrentDateIndex] = useState<number>(0);
  const [isShowingToday, setIsShowingToday] = useState<boolean>(false);

  // 缓存相关常量
  const CACHE_KEY = 'team_work_dates_cache';
  const CACHE_TIMESTAMP_KEY = 'team_work_dates_timestamp';
  const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

  useEffect(() => {
    loadAvailableDates();
  }, []);

  useEffect(() => {
    loadRecordsForDate(currentDate);
  }, [currentDate]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentDateIndex > 0) {
        const newIndex = currentDateIndex - 1;
        const newDate = availableDates[newIndex];
        setCurrentDateIndex(newIndex);
        setCurrentDate(newDate);
        setIsShowingToday(newDate === dayjs().format('YYYY-MM-DD'));
      } else if (event.key === 'ArrowRight' && currentDateIndex < availableDates.length - 1) {
        const newIndex = currentDateIndex + 1;
        const newDate = availableDates[newIndex];
        setCurrentDateIndex(newIndex);
        setCurrentDate(newDate);
        setIsShowingToday(newDate === dayjs().format('YYYY-MM-DD'));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [currentDateIndex, availableDates]);

  const loadAvailableDates = async (forceRefresh = false) => {
    setDatesLoading(true);
    try {
      let dates: string[] = [];
      
      // 检查缓存
      if (!forceRefresh) {
        const cachedDates = sessionStorage.getItem(CACHE_KEY);
        const cacheTimestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
        
        if (cachedDates && cacheTimestamp) {
          const now = Date.now();
          const timestamp = parseInt(cacheTimestamp);
          
          // 如果缓存未过期，使用缓存数据
          if (now - timestamp < CACHE_DURATION) {
            dates = JSON.parse(cachedDates);
            console.log('使用缓存的日期数据');
          }
        }
      }
      
      // 如果没有缓存或需要刷新，从API获取
      if (dates.length === 0) {
        console.log('从API获取日期数据');
        dates = await api.getTeamWorkDates();
        
        // 缓存数据
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(dates));
        sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      }
      
      setAvailableDates(dates);
      
      // 设置当前日期索引 - 优先显示今天，如果今天没有记录则显示最近的有记录日期
      const todayStr = dayjs().format('YYYY-MM-DD');
      const todayIndex = dates.findIndex(date => date === todayStr);
      
      if (todayIndex >= 0) {
        // 今天有记录，显示今天
        setCurrentDateIndex(todayIndex);
        setCurrentDate(todayStr);
        setIsShowingToday(true);
        console.log(`今天${todayStr}有工作记录，显示今天的数据`);
      } else if (dates.length > 0) {
        // 今天没有记录，显示最近的有记录日期（日期数组已按降序排列）
        setCurrentDateIndex(0);
        setCurrentDate(dates[0]);
        setIsShowingToday(false);
        console.log(`今天${todayStr}无记录，显示最近有记录的日期：${dates[0]}`);
      } else {
        setIsShowingToday(false);
        console.log('没有任何工作记录');
      }
    } catch (error) {
      console.error('加载可用日期失败:', error);
      message.error('加载日期失败');
    } finally {
      setDatesLoading(false);
    }
  };

  const loadRecordsForDate = async (date: string) => {
    setLoading(true);
    try {
      const data = await api.getTeamWorkRecords({
        start_date: date,
        end_date: date,
        skip: 0,
        limit: 100
      });
      setRecords(data);
    } catch (error: any) {
      console.error('加载记录失败:', error);
      if (error.response?.status === 403) {
        message.error('您没有权限查看团队工作记录');
      } else {
        message.error('加载记录失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshCache = () => {
    // 清除缓存并重新加载
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
    loadAvailableDates(true);
    message.success('日期缓存已刷新');
  };

  const columns: ColumnsType<WorkRecord> = [
    {
      title: '',
      key: 'record',
      render: (record: WorkRecord) => (
        <div>
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 500
          }}>
            <span>{record.user?.real_name || record.user?.username || '-'}</span>
            <span>{record.duration_hours?.toFixed(1) || '-'}小时</span>
            <span>¥{record.calculated_cost?.toFixed(2) || '0.00'}</span>
            <span>{record.project?.name || '-'}</span>
          </div>
          <div style={{ 
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#f8f8f8',
            borderRadius: '4px',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: '#666'
          }}>
            {record.description || '无具体描述'}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 日期导航显示 */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        position: 'relative'
      }}>
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
          {dayjs(currentDate).format('YYYY年MM月DD日')} ({dayjs(currentDate).format('dddd')})
          {!isShowingToday && currentDate !== dayjs().format('YYYY-MM-DD') && (
            <span style={{ 
              fontSize: '12px', 
              color: '#ff6b6b', 
              marginLeft: '8px', 
              fontWeight: 'normal' 
            }}>
              今日无记录
            </span>
          )}
          {isShowingToday && (
            <span style={{ 
              fontSize: '12px', 
              color: '#52c41a', 
              marginLeft: '8px', 
              fontWeight: 'normal' 
            }}>
              今日
            </span>
          )}
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
          使用 ← → 键浏览不同日期的记录 ({currentDateIndex + 1}/{availableDates.length})
          {!isShowingToday && availableDates.length > 0 && (
            <span style={{ color: '#999', marginLeft: '8px' }}>
              · 显示最近有记录的日期
            </span>
          )}
        </div>
        <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
          <Button 
            size="small" 
            icon={<ReloadOutlined />}
            loading={datesLoading}
            onClick={handleRefreshCache}
            title="刷新日期缓存"
          >
            刷新
          </Button>
        </div>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          showHeader={false}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description="该日期暂无团队工作记录"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default TeamLogs;